import { motion } from 'framer-motion';
import { 
  Building2, 
  CarFront, 
  Truck, 
  Store, 
  Wrench
} from 'lucide-react';

const industries = [
  {
    icon: Building2,
    title: 'Insurers',
    description: 'Audit-ready documentation, OEM compliance verification, and transparent reporting for claims management.',
  },
  {
    icon: CarFront,
    title: 'Collision Repairers',
    description: 'Seamless integration with your repair process. Quick turnaround times and direct communication.',
  },
  {
    icon: Truck,
    title: 'Fleet Operators',
    description: 'Volume calibration services with priority scheduling and consolidated reporting for fleet vehicles.',
  },
  {
    icon: Store,
    title: 'Dealerships',
    description: 'OEM-aligned calibration for new and used vehicle reconditioning and warranty compliance.',
  },
  {
    icon: Wrench,
    title: 'Mechanical Workshops',
    description: 'Partner with us for ADAS calibration referrals. White-label services available.',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5 },
  },
};

export const Industries = () => {
  return (
    <section
      id="industries"
      data-testid="industries-section"
      className="py-20 md:py-28 bg-slate-900"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-blue-400 text-sm font-semibold tracking-widest uppercase mb-4 block">
            Industries We Support
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4">
            Serving South Australia's
            <span className="block text-blue-400">Automotive Industry</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-base sm:text-lg">
            From major insurers to independent workshops, we provide calibration 
            services tailored to your needs.
          </p>
        </motion.div>

        {/* Industries Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {industries.map((industry, index) => {
            const Icon = industry.icon;
            return (
              <motion.div
                key={industry.title}
                variants={itemVariants}
                className={`bg-slate-800/50 border border-slate-700 rounded-lg p-6 hover:border-blue-500/50 hover:bg-slate-800 transition-all ${
                  index === 4 ? 'sm:col-span-2 lg:col-span-1' : ''
                }`}
                data-testid={`industry-card-${index}`}
              >
                <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-3">
                  {industry.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {industry.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
